import React from "react";
import {
  ChartBar,
  CheckCircle,
  Eye,
  PhoneCall,
  Storefront,
  Target,
  UsersThree,
  Wallet,
} from "@phosphor-icons/react";
import RghtnowMarketingHeader from "./RghtnowMarketingHeader";
import RghtnowMarketingFooter from "./RghtnowMarketingFooter";
import AnimatedStats from "../components/AnimatedStats";
import "./rghtnow-pages.css";

const upload = (path) => `https://rghtnow.com/wp-content/uploads/${path}`;

const heroFeatures = [
  {
    title: "Quick Response",
    text: "We aim to respond within minutes",
    icon: PhoneCall,
  },
  {
    title: "Customer Support",
    text: "Our support team is available round the clock",
    icon: UsersThree,
  },
  {
    title: "Trusted & Reliable",
    text: "Your satisfaction is our top priority",
    icon: CheckCircle,
  },
];

const whyItems = [
  {
    title: "Highly Targeted",
    text: "Reach the right audience actively looking for products & services.",
    icon: Target,
    tone: "green",
  },
  {
    title: "High Visibility",
    text: "Promote your brand across app, website & partner networks.",
    icon: Eye,
    tone: "orange",
  },
  {
    title: "Cost Effective",
    text: "Flexible advertising plans that fit every business size.",
    icon: Wallet,
    tone: "pink",
  },
  {
    title: "Increase Sales",
    text: "Drive traffic, generate leads and boost conversions.",
    icon: ChartBar,
    tone: "orange",
  },
];

const categoryOptions = ["Grocery", "Flower Shop", "Water", "Food", "Pet Shop", "Taxi"];
const skuOptions = ["Under 500", "500-1,000", "1,000-5,000", "5,000-15,000", "15,000"];
const groupOptions = ["Individual", "Small Chain (2-5)", "Group (6-20)", "Large Group (20+)"];
const businessTypeOptions = ["Modern (POS/Computer)", "Traditional", "Hybrid"];

const SelectField = ({ label, options }) => {
  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <label className="rght-field" htmlFor={id}>
      <span>{label}</span>
      <select id={id} defaultValue="">
        <option value="" disabled>
          Select
        </option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
};

const TextField = ({ label, placeholder, type = "text" }) => {
  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <label className="rght-field" htmlFor={id}>
      <span>{label}</span>
      <input id={id} type={type} placeholder={placeholder} />
    </label>
  );
};

const OnboardYourStorePage = () => (
  <div className="rght-page onboard-your-store-page">
    <RghtnowMarketingHeader active="onboard" />

    <main>
      <section className="rght-hero rght-seller-hero">
        <div className="container rght-hero-grid">
          <div className="rght-hero-copy">
            <span className="rght-kicker">Onboard Your Store</span>
            <h1>
              Become a Seller on <em>RGHT NOW</em>
            </h1>
            <p>
              Start selling to millions of customers across multiple categories.
              <br />
              Grow your business with our powerful platform and tools.
            </p>
            <div className="rght-hero-feature-grid rght-hero-feature-stack">
              {heroFeatures.map((item) => {
                const Icon = item.icon;

                return (
                  <div className="rght-hero-feature" key={item.title}>
                    <span>
                      <Icon size={22} weight="fill" />
                    </span>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="rght-hero-art">
            <img src={upload("2026/06/seller-1024x829.png")} alt="RGHT NOW seller onboarding" />
          </div>
        </div>
      </section>

      <section className="rght-section rght-intake-section" id="merchant-onboarding">
        <div className="container rght-intake-grid">
          <form className="rght-form-card rght-intake-card" onSubmit={(event) => event.preventDefault()}>
            <div className="rght-form-title">
              <h2>Merchant Onboarding</h2>
              <p>Fill in your details below and our team will get in touch with you</p>
            </div>
            <div className="rght-field-grid">
              <TextField label="Business Name" placeholder="Enter Business name" />
              <TextField label="Owner Name" placeholder="Enter owner name" />
              <TextField label="Mobile / WhatsApp number" placeholder="Enter Mobile / WhatsApp number" type="tel" />
              <SelectField label="Business Category" options={categoryOptions} />
              <SelectField label="SKUs Held" options={skuOptions} />
              <SelectField label="Group/Individual" options={groupOptions} />
              <SelectField label="Business Type" options={businessTypeOptions} />
              <TextField label="Area / Location" placeholder="Enter Area / Location" />
              <TextField label="Email" placeholder="Email" type="email" />
              <label className="rght-field rght-native-file-field" htmlFor="upload-menu-catalog">
                <span>Upload Menu/Catalog (Optional)</span>
                <input id="upload-menu-catalog" type="file" accept=".jpg,.jpeg,.gif,.png,.pdf" />
              </label>
              <div className="rght-field">
                <span>Do you have delivery services?</span>
                <div className="rght-choice-list rght-choice-list-auto">
                  {["Yes", "No"].map((option) => (
                    <label key={option}>
                      <input name="delivery-services" type="radio" value={option} />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <button className="rght-button rght-button-pink rght-submit-button" type="submit">
              Get Started Now!
            </button>
          </form>

          <aside className="rght-why-panel">
            <h2>
              Why Sell on <em>RGHT NOW?</em>
            </h2>
            <div className="rght-why-list">
              {whyItems.map((item) => {
                const Icon = item.icon;

                return (
                  <article className="rght-why-item" key={item.title}>
                    <span className={`rght-why-icon ${item.tone}`}>
                      <Icon size={28} weight="fill" />
                    </span>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </article>
                );
              })}
            </div>
            <div className="rght-sidebar-cta">
              <h3>Ready to Grow Your Business?</h3>
              <p>Drive traffic, generate leads and boost conversions.</p>
              <img src={upload("2026/05/shop-rghtnow.svg")} alt="" />
            </div>
          </aside>
        </div>
      </section>

      <AnimatedStats subtitle="Onboard your store into a marketplace designed for repeated orders, category growth, and local discovery." />

      <section className="rght-seller-strip">
        <div className="container rght-seller-inner">
          <div>
            <h2>Start Selling on RGHT NOW Today!</h2>
            <p>More orders. More customers. More growth.</p>
          </div>
          <div className="rght-store-badges">
            <img src={upload("2023/09/Google_Play_Store_badge_EN.svg-1024x303.png")} alt="Google Play" />
            <img src={upload("2023/09/Download_on_the_App_Store_Badge.svg-1024x304.webp")} alt="App Store" />
          </div>
          <Storefront size={50} weight="fill" />
        </div>
      </section>
    </main>

    <RghtnowMarketingFooter />
  </div>
);

export default OnboardYourStorePage;
